import type { SocketEvents } from '@thinkalpha/ui-types/contracts/push';
import { io } from 'socket.io-client';
import type { Socket } from 'socket.io-client';
import { appConfig } from 'src/lib/config';
import { CONNECTION_OPTIONS } from 'src/lib/socketConnectionOptions';
import { container } from 'src/StaticContainer';

const log = container.get('Logger').getSubLogger({ name: 'push:authentication' });

// Event map for typed events - only known message types
interface PushServiceEventMap {
    // Authentication events
    'auth.success': (payload?: any) => void;
    'auth.failed': (payload?: any) => void;

    // Socket events
    connect: () => void;
    disconnect: () => void;

    // Application events
    [SocketEvents.activeApp]: (payload: { appId: string; userId: string }) => void;
    [SocketEvents.checkAppVersion]: () => void;
}

// Emit event map - only known message types
interface PushServiceEmitMap {
    auth: (accessToken: string) => void;
}

export interface PushServiceSocket {
    on<K extends keyof PushServiceEventMap>(event: K, listener: PushServiceEventMap[K]): void;
    off<K extends keyof PushServiceEventMap>(event: K, listener: PushServiceEventMap[K]): void;
    emit<K extends keyof PushServiceEmitMap>(event: K, ...args: Parameters<PushServiceEmitMap[K]>): void;
    setAccessToken(accessToken: string | null): void;
}

class PushServiceSocketImpl implements PushServiceSocket {
    #accessToken: string | null = null;
    #socket: Socket;

    constructor() {
        this.#socket = io(appConfig.api, CONNECTION_OPTIONS);
        this.#setupEventListeners();

        // TODO: Do not use window object to detect browser/native, use `isNative`
        if (typeof window !== 'undefined') (window as any)['socket'] = this.#socket;
    }

    #setupEventListeners(): void {
        this.#socket.on('connect', () => {
            log.info({ message: 'Connected to platform socket.io' });
            if (this.#accessToken) {
                this.emit('auth', this.#accessToken);
            }
        });
        this.#socket.on('auth.failed', (payload: any) => {
            log.warn({ message: 'Authentication to platform socket.io failed', payload });
        });
        this.#socket.on('auth.success', (payload: any) => {
            log.info({ message: 'Authentication to platform socket.io succeeded', payload });
        });
    }

    on<K extends keyof PushServiceEventMap>(event: K, listener: PushServiceEventMap[K]): void {
        this.#socket.on(event as string, listener as any);
    }

    off<K extends keyof PushServiceEventMap>(event: K, listener: PushServiceEventMap[K]): void {
        this.#socket.off(event as string, listener as any);
    }

    emit<K extends keyof PushServiceEmitMap>(event: K, ...args: Parameters<PushServiceEmitMap[K]>): void {
        this.#socket.emit(event as string, ...args);
    }

    setAccessToken(accessToken: string | null): void {
        this.#accessToken = accessToken;
        if (accessToken === null) {
            log.info({ message: 'Clearing socket.io authentication' });
            this.#socket.disconnect();
            return;
        }

        log.info({ message: 'Authenticating with socket.io' });
        if (this.#socket.connected) {
            this.emit('auth', accessToken);
        } else {
            this.#socket.connect();
        }
    }
}

export const pushServiceSocket = new PushServiceSocketImpl();

// Separate socket instance for session barrier that only uses regular access tokens (no masquerading)
export const sessionBarrierSocket = new PushServiceSocketImpl();
